import { useState } from "react";
import { ManaText } from "@/components/common/ManaSymbol";
import { Crown, Minus, Plus, Shield, Swords, Target } from "lucide-react";
import { CardFace } from "@/components/companion/CardFace";

function Rules({ rules }) {
  return (
    <details className="text-xs text-slate-400" data-testid="schemer-rules">
      <summary className="cursor-pointer text-aether-300">Homebrew Schemer rules</summary>
      <ul className="mt-2 list-disc space-y-1 pl-4">{(rules || []).map((r) => <li key={r}>{r}</li>)}</ul>
    </details>
  );
}

function Counters({ act, k, isHost }) {
  const [reason, setReason] = useState("effect");
  const [explicit, setExplicit] = useState(false);
  const [setTo, setSetTo] = useState("");
  const go = (delta) => act("schemer_counters", { deck: k, delta, reason, explicit });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`schemer-counters-${k}`}>
      <select className="field w-auto py-1 text-xs" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`schemer-counter-reason-${k}`}>
        <option value="effect">Card effect</option><option value="proliferate">Proliferate</option></select>
      {reason === "proliferate" && <label className="flex items-center gap-1 text-[11px] text-slate-300"><input type="checkbox" checked={explicit} onChange={(e) => setExplicit(e.target.checked)} data-testid={`schemer-explicit-${k}`} />Effect explicitly allows Schemers</label>}
      <button className="btn-outline px-2 py-1" onClick={() => go(-1)} aria-label="Remove a loyalty counter" data-testid={`schemer-minus-${k}`}><Minus className="h-3 w-3" /></button>
      <button className="btn-outline px-2 py-1" onClick={() => go(1)} aria-label="Add a loyalty counter" data-testid={`schemer-plus-${k}`}><Plus className="h-3 w-3" /></button>
      {isHost && <><input className="field w-16 py-1 text-xs" value={setTo} onChange={(e) => setSetTo(e.target.value.replace(/\D/g, ""))} placeholder="Set" data-testid={`schemer-set-input-${k}`} />
        <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setTo && act("schemer_counters", { deck: k, set: +setTo })} data-testid={`schemer-set-button-${k}`}>Host: set</button></>}
    </div>
  );
}

function Combat({ act, k }) {
  const [amount, setAmount] = useState("");
  const [text, setText] = useState("");
  const [combat, setCombat] = useState(true);
  return (
    <div className="space-y-2" data-testid={`schemer-combat-${k}`}>
      <input className="field py-1.5 text-xs" maxLength={60} placeholder="Source / attackers (optional)" value={text} onChange={(e) => setText(e.target.value)} data-testid={`schemer-source-${k}`} />
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-outline px-2 py-1 text-xs" onClick={() => act("schemer_attack", { deck: k, attackers: text })} data-testid={`schemer-attack-${k}`}><Swords className="h-3 w-3" />Attacked</button>
        <button className="btn-outline px-2 py-1 text-xs" onClick={() => act("schemer_target", { deck: k, source: text })} data-testid={`schemer-target-${k}`}><Target className="h-3 w-3" />Targeted</button>
        <input className="field w-16 py-1 text-xs" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} placeholder="Dmg" aria-label="Damage amount" data-testid={`schemer-damage-input-${k}`} />
        <label className="flex items-center gap-1 text-[11px] text-slate-300"><input type="checkbox" checked={combat} onChange={(e) => setCombat(e.target.checked)} data-testid={`schemer-combat-check-${k}`} />Combat</label>
        <button className="btn-gold px-3 py-1 text-xs" onClick={() => amount && act("schemer_damage", { deck: k, amount: +amount, source: text, combat }).then(() => setAmount(""))} data-testid={`schemer-damage-button-${k}`}>Deal damage</button>
      </div>
    </div>
  );
}

export function SchemerTracker({ s, act, k, e, mine, seated }) {
  const c = s.cards[e.inst];
  const abilities = c.custom_properties?.schemer_abilities || [];
  const used = e.used_turn === s.game.turn ? e.uses : 0;
  return (
    <div className="flex flex-wrap gap-4" data-testid={`schemer-zone-${k}`}>
      <CardFace c={c} size="md" label="Legendary Schemer" testid={`schemer-card-${e.inst}`} />
      <div className="min-w-[16rem] flex-1 space-y-3">
        <div className="flex items-end gap-4">
          <p className="flex items-center gap-2 font-display text-5xl text-amber-100" data-testid={`schemer-loyalty-${k}`}><Shield className="h-8 w-8 text-aether-300" />{e.loyalty}</p>
          <p className="pb-2 font-mono text-xs text-slate-400">start {e.start} · damage taken {e.damage} · abilities used this turn {used}/{s.rules.archenemy.schemer_activations || "∞"}</p>
        </div>
        {c.custom_properties?.schemer_restrictions && <p className="text-xs text-amber-200" data-testid={`schemer-restrictions-${k}`}>Restrictions: {c.custom_properties.schemer_restrictions}</p>}
        <div className="space-y-1">{abilities.map((a, i) => (
          <button key={i} disabled={!mine} className="flex w-full items-start gap-2 rounded border border-[#2A3447] p-2 text-left text-xs text-slate-200 transition-colors hover:border-amber-600 disabled:opacity-60" onClick={() => act("schemer_ability", { deck: k, index: i })} data-testid={`schemer-ability-${k}-${i}`}>
            <span className="rounded bg-amber-900/50 px-1.5 font-mono text-amber-200">{a.cost}</span><ManaText text={a.text} /></button>))}
          {!abilities.length && <p className="text-xs text-slate-500">No loyalty abilities defined.</p>}</div>
        {seated && <Combat act={act} k={k} />}
        {seated && <Counters act={act} k={k} isHost={s.is_host} />}
        {mine && <button className="btn-outline px-3 py-1 text-xs" onClick={() => act("finish_scheme", { inst: e.inst })} data-testid={`schemer-abandon-${k}`}><Crown className="h-3 w-3" />Abandon Schemer (to bottom)</button>}
        <Rules rules={s.schemer_rules} />
      </div>
    </div>
  );
}
